import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/components';
import { useExportJob } from '@/hooks';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { ExportType } from '@/types/export';
import type { ReportEvent, ReportWithEvents } from '@/types/report';
import { toSafeExportFileName } from '@/utils/file-name';
import { SELECTION_FILE_NAME_SUFFIX } from './useReportExport';

const EXPORT_TOAST_ID = 'report-zip-export';

interface UseReportZipExportParams {
    report: ReportWithEvents | undefined;
}

interface UseReportZipExportResult {
    isBusy: boolean;
    exportable: boolean;
    startExport: (events: ReportEvent[]) => Promise<boolean>;
}

export function useReportZipExport({
    report,
}: UseReportZipExportParams): UseReportZipExportResult {
    const { t } = useTranslation(I18nNamespace.REPORTS);
    // Memoized so useExportJob's polling effect keeps stable dependencies.
    const messages = useMemo(
        () => ({
            starting: t('detail.export.zipStarting'),
            progress: (progress: number) =>
                t('detail.export.zipProgress', { progress }),
            ready: t('detail.export.zipReady'),
            readySkipped: (skippedCount: number) =>
                t('detail.export.zipReadySkipped', { count: skippedCount }),
            failed: t('detail.export.failed'),
        }),
        [t],
    );
    const { busy, start } = useExportJob({
        toastId: EXPORT_TOAST_ID,
        messages,
    });

    const exportable = report?.hasDownloadableEvents ?? false;

    const startExport = useCallback(
        async (events: ReportEvent[]): Promise<boolean> => {
            if (!report) {
                toast.error(t('detail.export.unavailable'));
                return false;
            }
            if (!exportable) {
                toast.error(t('detail.export.zipUnavailable'));
                return false;
            }
            if (!events.length) {
                toast.error(t('detail.export.failed'));
                return false;
            }
            const archiveBaseName = toSafeExportFileName(report.name, 'report');
            const eventIds = events.map((event) => event.id);
            const isPartial = eventIds.length !== report.events.length;
            return start(
                ExportType.REPORT,
                {
                    reportId: report.id,
                    eventOrder: eventIds,
                    eventIds,
                },
                isPartial
                    ? `${archiveBaseName}${SELECTION_FILE_NAME_SUFFIX}.zip`
                    : `${archiveBaseName}.zip`,
            );
        },
        [exportable, report, start, t],
    );

    return { isBusy: busy, exportable, startExport };
}
